import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  SarrafiyeCalc,
  TrLinkMatrix,
  FaqAccordion,
  buildTrSchema,
  trDateParts,
  goldRatesQueryOptions,
  useGramTry,
  formatTry,
  SARRAFIYE,
} from "@/components/tr/shell";

const SLUG = "/tr/ceyrek-cumhuriyet-altin-fiyatlari";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "Çeyrek altın kaç para ve eski-yeni tarihli çeyrek altın farkı nedir?", a: "Çeyrek altın 22 ayar, yaklaşık 1.75 gram ağırlığındadır. Eski tarihli çeyrekler (darphane baskısı yıllara göre) numismatik değer taşıdığında serbest piyasa fiyatının %5-15 üzerinde işlem görebilir. Standart yeni çeyrek fiyatı 22 ayar gram × 1.75 formülüyle hesaplanır." },
  { q: "Cumhuriyet altını fiyatı bugün serbest piyasada ne kadar?", a: "Cumhuriyet Altını (Ata Lira) 22 ayar, 7.216 gram ağırlığındadır. Serbest piyasa değeri = 22 ayar gram altın satış × 7.216, üzerine %1-3 arasında darphane primi eklenir. Yukarıdaki tabloda hem Cumhuriyet hem Ata altının anlık TL değeri listelenir." },
  { q: "Çeyrek altın kaç gramdır ve Cumhuriyet altını ile farkı nedir?", a: "Çeyrek altın 1.75 gram, yarım altın 3.5 gram, tam altın 7 gram ve Cumhuriyet altını 7.216 gram gelir. Tümü 22 ayardır. Cumhuriyet altınının tam altından farkı yalnızca 0.216 gramdır — üzerindeki Atatürk portresi nedeniyle koleksiyonel değeri daha yüksektir." },
];

export const Route = createFileRoute("/tr/ceyrek-cumhuriyet-altin-fiyatlari")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = trDateParts();
    const title = `Çeyrek, Yarım, Tam ve Cumhuriyet Altını Fiyatları Bugün — Canlı Sarrafiye Kuru`;
    const description = `Bugün çeyrek altın ne kadar, cumhuriyet altını, yarım ve tam altın fiyatı serbest piyasada anlık kuruş kuruş TL cinsinden burada. Ata altın ve darphane baskı farkları dahil.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "tr-TR" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "tr_TR" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "tr-TR", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildTrSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: SarrafiyePage,
});

function SarrafiyePage() {
  const dp = trDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Türkiye · Sarrafiye</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          Bugün Çeyrek, Yarım, Tam, Cumhuriyet ve Ata Altın Fiyatları ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          Sarrafiye altınları 22 ayar standardında basılır ve fiyatları darphane primi ile serbest piyasa gram altın kuruna bağlıdır. Aşağıdaki hesaplayıcı çeyrek, yarım, tam, Cumhuriyet ve Ata altınının anlık TL karşılığını gösterir.
        </p>
      </header>
      <LiveChart title={`Sarrafiye Altın Fiyat Grafiği — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <SarrafiyeMultiplierTable />
      <SarrafiyeCalc />
      <FaqAccordion items={FAQS} />
      <TrLinkMatrix current={SLUG} />
    </main>
  );
}

function SarrafiyeMultiplierTable() {
  const { gram22 } = useGramTry();
  const rows = [
    { name: "Çeyrek Altın", w: SARRAFIYE.ceyrek },
    { name: "Yarım Altın", w: SARRAFIYE.yarim },
    { name: "Tam Altın", w: SARRAFIYE.tam },
    { name: "Cumhuriyet Altını", w: SARRAFIYE.cumhuriyet },
    { name: "Ata Altın", w: SARRAFIYE.ata },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">Sarrafiye Altın Ağırlık ve Fiyat Tablosu</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">Altın Türü</th>
              <th className="px-3 py-2 text-right">Gram Karşılığı</th>
              <th className="px-3 py-2 text-right">Serbest Piyasa Satış</th>
              <th className="px-3 py-2 text-right">Kuyumcu (%2 makas)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const serbest = gram22 * r.w;
              const kuyumcu = serbest * 1.02;
              return (
                <tr key={r.name} className="border-t border-amber-500/10">
                  <th className="px-3 py-2 text-left font-medium">{r.name}</th>
                  <td className="px-3 py-2 text-right">{r.w} g</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700 dark:text-emerald-300">{formatTry(serbest)}</td>
                  <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-300">{formatTry(kuyumcu)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}